import React, { useState, useEffect, useMemo } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Box,
  Pagination,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  CircularProgress,
} from '@mui/material';
import { saveAs } from 'file-saver';
import { useNavigate } from 'react-router-dom';
import '../../styles/UserStyles.css';
import '../../styles/App.scss';
import {
  ADD_CUSTOMER,
  BOOKING_AIRPORT_DETAILS,
  EDIT_CUSTOMER,
  VIEW_CUSTOMER,
} from '../../component/common/Routes/AppRoutes';
import ReusableTable from '../../component/ui/ReusableTable';
import { customHeaders } from './CustomHeaders';
import { customerColumnFetcher } from '../../Style';
import SearchAndButtonTab from '../../component/ui/SearchAndButtonTab';
import { useDispatch, useSelector } from 'react-redux';
import {
  deleteCustomerRequest,
  fetchAllCustomersRequest,
  resetCustomerState,
} from '../../redux/Customers/actions/customerActions';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Loading from '../../component/ui/Loading';
import CustomButton from '../../component/ui/FormFields/Button';
import {
  VENDOR_ADD_CUSTOMER,
  VENDOR_EDIT_CUSTOMER,
  VENDOR_VIEW_CUSTOMER,
} from '../../component/common/Routes/VendorRoutes';

import { fetchCustomersByVendorRequest } from '../../redux/Vendors/actions/vendorActions';

const CustomerManagementList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchColumn, setSearchColumn] = useState('serialNo');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const rowsPerPage = 5;

  const user = useSelector((state) => state.auth.user);

  const {
    customers: response,
    success,
    loading,
    error,
  } = useSelector((state) =>
    user?.user_type === 2 ? state.vendors : state.customers
  );

  const loadings = useSelector((state) => state.customers?.customers?.loading);

  const viewListLoading = loadings;
  const customers = response?.data || [];
  const totalSize = response?.totalRecords;
  const deleteSuccess = success?.delete;
  const customerDetailsState = useSelector(
    (state) => state.customers?.customerDetails
  );

  const deleteLoading = customerDetailsState?.deleteLoading;

  useEffect(() => {
    if (deleteSuccess) {
      toast.success('Customer Deleted Successfully', {
        position: 'top-center',
        autoClose: 3000,
        onClose: () => {
          dispatch(resetCustomerState());
          if (user?.user_type === 2) {
            dispatch(
              fetchCustomersByVendorRequest({
                pagination_required: false,
                pagesize: 10,
                pageNo: 1,
                vendor_id: user?.vendor_id,
              })
            );
          } else {
            dispatch(fetchAllCustomersRequest());
          }
        },
      });
    }
  }, [deleteSuccess]);

  useEffect(() => {
    if (user?.user_type === 2) {
      dispatch(
        fetchCustomersByVendorRequest({
          pagination_required: false,
          pagesize: 10,
          pageNo: 1,
          vendor_id: user?.vendor_id,
        })
      );
    } else {
      dispatch(fetchAllCustomersRequest());
    }
  }, []);

  const customerDetails = customerDetailsState?.data || {};
  const [open, setOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);

  const handleExport = () => {
    if (!customers.length) return;

    const csvHeaders = [
      'First Name,Last Name,Display Name,Email,Phone,Address1,Address2,City,State,Country,Pincode,Status',
    ];

    const csvRows = customers.map((customer) => {
      // Properly escape fields that might contain commas by wrapping them in quotes
      const escapeCsvField = (field) => {
        if (field === null || field === undefined) return '""';
        const stringField = String(field || '-');
        // If the field contains commas, quotes, or newlines, wrap it in quotes and escape any quotes
        if (
          stringField.includes(',') ||
          stringField.includes('"') ||
          stringField.includes('\n')
        ) {
          return `"${stringField.replace(/"/g, '""')}"`;
        }
        return stringField;
      };

      return [
        escapeCsvField(customer.first_name),
        escapeCsvField(customer.last_name),
        escapeCsvField(`${customer.first_name} ${customer.last_name}`),
        escapeCsvField(customer.email),
        escapeCsvField(customer.phone),
        escapeCsvField(customer.address1),
        escapeCsvField(customer.address2),
        escapeCsvField(customer.city_name),
        escapeCsvField(customer.state_name),
        escapeCsvField(customer.country_name),
        escapeCsvField(customer.pincode),
        escapeCsvField(customer.status === 0 ? 'Active' : 'Inactive'),
      ].join(',');
    });

    const csvContent = [csvHeaders, ...csvRows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, 'customers.csv');
  };

  const redirectToBooking = (customer) => {
    navigate(BOOKING_AIRPORT_DETAILS, {
      state: {
        customerDetails: customer,
      },
    });
  };

  const handleOpenPopup = (customer) => {
    setCustomerToDelete(customer);
    setOpen(true);
  };

  const handleClosePopup = () => {
    setCustomerToDelete(null);
    setOpen(false);
  };

  const handleConfirmDelete = () => {
    if (customerToDelete) {
      setOpen(false);
      dispatch(deleteCustomerRequest(customerToDelete.id));
      dispatch(fetchAllCustomersRequest());
    }
    handleClosePopup();
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const filteredCustomersForSearch = useMemo(() => {
    const transformedCustomers = customers.map((customer) => ({
      customerId: customer?.customer_serial_no,
      id: customer?.id,
      fullName: `${customer?.first_name ?? ''} ${
        customer?.last_name ?? ''
      }`.trim(),
      email: customer?.email,
      mobile: customer?.phone,
    }));

    if (!searchQuery || !searchColumn) {
      return transformedCustomers;
    }

    if (searchColumn === 'id') {
      return transformedCustomers;
    }

    return transformedCustomers.filter((customer) => {
      const value = customer[searchColumn];
      return value
        ?.toString()
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
    });
  }, [customers, searchColumn, searchQuery]);

  const filteredCustomersForTable = useMemo(() => {
    const transformedCustomers = customers.map((customer) => ({
      customerId: customer?.customer_serial_no,
      id: customer?.id,
      fullName: `${customer?.first_name ?? ''} ${
        customer?.last_name ?? ''
      }`.trim(),
      email: customer?.email,
      mobile: customer?.phone,
      lastActive: customer?.last_active !== null ? customer?.last_active : '-',
      createdDate: customer?.created_at,
      status: customer?.status === 0 ? 'Active' : 'Inactive',
    }));

    if (!searchQuery || !searchColumn) {
      return transformedCustomers;
    }

    return transformedCustomers.filter((customer) => {
      if (searchColumn === 'customerId' && customer.customerId) {
        return customer.customerId
          .toString()
          .toLowerCase()
          .includes(searchQuery.toLowerCase());
      } else if (searchColumn === 'fullName' && customer.fullName) {
        return customer.fullName
          .toLowerCase()
          .includes(searchQuery.toLowerCase());
      } else if (searchColumn === 'email' && customer.email) {
        return customer.email.toLowerCase().includes(searchQuery.toLowerCase());
      } else if (searchColumn === 'mobile' && customer.mobile) {
        return customer.mobile
          .toString()
          .toLowerCase()
          .includes(searchQuery.toLowerCase());
      }
      return false;
    });
  }, [customers, searchColumn, searchQuery]);

  const columns = useMemo(() => {
    if (filteredCustomersForSearch.length > 0) {
      const columnData = customerColumnFetcher(filteredCustomersForSearch);

      if (Array.isArray(columnData)) {
        return columnData.filter((column) =>
          typeof column === 'string' ? column !== 'id' : column.value !== 'id'
        );
      } else if (columnData && typeof columnData === 'object') {
        const result = { ...columnData };

        if (Array.isArray(result[0])) {
          result[0] = result[0].filter((col) => col !== 'id');
        }

        if (Array.isArray(result[1])) {
          result[1] = result[1].filter((col) => col.value !== 'id');
        }

        return result;
      }
      return columnData;
    }
    return [[], []];
  }, [filteredCustomersForSearch]);

  const paginatedData = useMemo(() => {
    return filteredCustomersForTable.slice(
      (page - 1) * rowsPerPage,
      page * rowsPerPage
    );
  }, [filteredCustomersForTable, page, rowsPerPage]);

  if (viewListLoading) {
    return <Loading text='Loading Customer Details...' />;
  }

  return (
    <>
      <ToastContainer />
      <Grid container spacing={5}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <SearchAndButtonTab
                title='Customers'
                columns={columns}
                searchColumn={searchColumn}
                setSearchColumn={setSearchColumn}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                handleExport={handleExport}
                routeName={
                  user.role === 'Vendor' ? VENDOR_ADD_CUSTOMER : ADD_CUSTOMER
                }
                type='customer'
              />
              {loading ? (
                <Box textAlign='center' py={5}>
                  <CircularProgress />
                </Box>
              ) : error ? (
                <Box textAlign='center' py={5}>
                  <p>Error: {error}</p>
                </Box>
              ) : (
                <>
                  <ReusableTable
                    data={filteredCustomersForTable}
                    headers={customHeaders}
                    paginatedData={paginatedData}
                    redirectToBooking={redirectToBooking}
                    navigate={navigate}
                    title={
                      user.role === 'Admin' || user.role === 'Staff'
                        ? 'Customer'
                        : ''
                    }
                    handleView={(customer) => {
                      const viewRoute =
                        user.role === 'Vendor'
                          ? `${VENDOR_VIEW_CUSTOMER.replace(
                              ':customerId',
                              customer.id
                            )}`
                          : `${VIEW_CUSTOMER.replace(
                              ':customerId',
                              customer.id
                            )}`;
                      navigate(viewRoute);
                    }}
                    handleEdit={(customer) => {
                      const editRoute =
                        user.role === 'Vendor'
                          ? `${VENDOR_EDIT_CUSTOMER.replace(
                              ':customerId',
                              customer.id
                            )}`
                          : `${EDIT_CUSTOMER.replace(
                              ':customerId',
                              customer.id
                            )}`;
                      navigate(editRoute);
                    }}
                    handleOpenPopup={handleOpenPopup}
                  />
                  <Box className='button-box'>
                    <Pagination
                      count={Math.ceil(
                        filteredCustomersForTable.length / rowsPerPage
                      )}
                      page={page}
                      onChange={handlePageChange}
                      color='primary'
                    />
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Dialog open={open} onClose={handleClosePopup}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete{' '}
              <strong>{customerToDelete?.fullName}</strong>?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              size='small'
              onClick={handleClosePopup}
              className='handle-back'
            >
              Cancel
            </Button>
            <CustomButton
              variant='contained'
              className='handle-delete'
              type='submit'
              size='small'
              loading={deleteLoading}
              text={'Delete'}
              onClick={handleConfirmDelete}
            />
          </DialogActions>
        </Dialog>
      </Grid>
    </>
  );
};
export default CustomerManagementList;
